"use client";

import { useLocale, useTranslations } from "next-intl";
import React, { useContext, useEffect, useRef, useState } from "react";

import CreateLeadForm from "@/components/CreateLeadModal/CreateLeadForm";
import { ModalsContext } from "@/context/ModalsContext";
import useMediaQuery from "@/hooks/useMediaQuery";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "../ui/Button/Button";
import Icon from "../ui/Icon/Icon";

interface BlogLeadFormSidebarProps {
  leadFormPageLocation: string;
}

export default function BlogLeadFormSidebar({ leadFormPageLocation }: BlogLeadFormSidebarProps) {
  const locale = useLocale();
  const [language, _] = locale.split("-");
  const isRtlLocale = language === "ar";
  const isMobile = useMediaQuery("(max-width: 600px)");
  const t = useTranslations();
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const [positionStyle, setPositionStyle] = useState<React.CSSProperties>({});
  const { setGetInTouchModalOpen } = useContext(ModalsContext);
  useEffect(() => {
    const TOP_OFFSET = 100;
    const SIDE_SHIFT = 50;
    const directionalShift = isRtlLocale ? -SIDE_SHIFT : SIDE_SHIFT;

    const handlePosition = () => {
      const sidebar = sidebarRef.current;
      if (!sidebar) return;

      const parent = sidebar.closest("[data-blog-body-layout]") as HTMLElement | null;
      const slot = sidebar.closest("[data-blog-sidebar-slot]") as HTMLElement | null;
      if (!parent || !slot) return;

      const parentRect = parent.getBoundingClientRect();
      const slotRect = slot.getBoundingClientRect();
      const parentTopDoc = parentRect.top + window.scrollY;
      const parentBottomDoc = parentTopDoc + parent.offsetHeight;
      const slotLeftRelativeToParent = slotRect.left - parentRect.left;
      const sidebarHeight = sidebar.offsetHeight;
      // Keep the sidebar slot in the normal flow so parent height does not collapse.
      slot.style.minHeight = `${sidebarHeight}px`;
      const startFixAt = parentTopDoc - TOP_OFFSET;
      const endFixAt = parentBottomDoc - sidebarHeight - TOP_OFFSET;

      let nextStyle: React.CSSProperties;

      if (window.scrollY < startFixAt) {
        nextStyle = {
          position: "absolute",
          top: "0px",
          left: `${slotLeftRelativeToParent + directionalShift}px`,
          width: `${slotRect.width}px`,
        };
      } else if (window.scrollY >= endFixAt) {
        const finalTop = Math.max(parent.offsetHeight - sidebarHeight, 0);
        nextStyle = {
          position: "absolute",
          top: `${finalTop}px`,
          left: `${slotLeftRelativeToParent + directionalShift}px`,
          width: `${slotRect.width}px`,
        };
      } else {
        nextStyle = {
          position: "fixed",
          top: `${TOP_OFFSET}px`,
          left: `${slotRect.left + directionalShift}px`,
          width: `${slotRect.width}px`,
        };
      }

      setPositionStyle((prev) => {
        if (prev.position === nextStyle.position && prev.top === nextStyle.top && prev.left === nextStyle.left && prev.width === nextStyle.width) {
          return prev;
        }

        return nextStyle;
      });
    };

    handlePosition();
    window.addEventListener("scroll", handlePosition);
    window.addEventListener("resize", handlePosition);

    return () => {
      window.removeEventListener("scroll", handlePosition);
      window.removeEventListener("resize", handlePosition);
    };
  }, [isRtlLocale]);

  return isMobile ? (
    <div className="start-chat-holder shadow-2xl" style={{ bottom: "174px", right: "13px", marginBottom: 0, zIndex: 60 }}>
      <Button
        rounded
        size={ButtonSize.MEDIUM}
        theme={ButtonTheme.GOLD}
        type="button"
        variant={ButtonVariant.PRIMARY}
        onClick={() => setGetInTouchModalOpen(true)}
        onTouchStart={() => setGetInTouchModalOpen(true)}
      >
        <Icon size="24" name="envelope.svg" title="chat" className="" />
      </Button>
    </div>
  ) : (
    <div data-blog-sidebar-slot className="hidden md:block md:w-[350px]">
      <div
        ref={sidebarRef}
        className="rounded-[10px] bg-gray-300 p-m overflow-visible"
        style={{ ...positionStyle, backgroundColor: "#F8F7F7", minHeight: "auto" }}
      >
        <div className="flex flex-col gap-s">
          <h2 className="font-primary font-medium text-m uppercase">{t("getInTouch.modal.title")}</h2>

          <p className="text-s font-secondary">{t("getInTouch.modal.subtitle")}</p>

          <CreateLeadForm mode="getInTouch" closeModal={() => {}} leadFormPageLocation={leadFormPageLocation} isStickleadForm={true} />
        </div>
      </div>
    </div>
  );
}
